import {createApp, ref} from 'vue';
import {connectVue} from '../modules/configureBugsnag';
import configureGlobalComponents from '../modules/configureGlobalComponents';
import axiosClient from '../modules/axiosClient';
import CircleLoader from '../components/circle-loader.vue';
import adverts from '../modules/adverts';
import {targetingCookiesAllowed} from '../helpers/cookie';
import useVuelidate from '@vuelidate/core';
import {phone, postcode} from '../modules/regitValidators';
import {email, helpers, required, sameAs} from '@vuelidate/validators';
import Checkbox from '../components/global/checkbox.vue';

const app = createApp({
    components: {
        'circle-loader': CircleLoader,
        'checkbox': Checkbox,
    },
    props: {
        make: String,
        makeName: String,
        model: String,
        modelName: String,
        intent: String,
        step: String,
        defaultImages: Array,
    },
    setup() {
        const vehicle = ref({
            intent: '',
            make: '',
            model: '',
        });

        const user = ref({
            name: '',
            email: '',
            phone: '',
            postcode: '',
            gdpr: false,
        });

        const v$ = useVuelidate({
            intent: {
                required: helpers.withMessage('Please select an intent', required),
            },
            make: {
                required: helpers.withMessage('Please select a make', required),
            },
            model: {
                required: helpers.withMessage('Please select a model', required),
            },
        }, vehicle);

        const v2$ = useVuelidate({
            name: {
                required: helpers.withMessage('Please enter your name', required),
            },
            email: {
                required: helpers.withMessage('Please enter your email', required),
                email,
            },
            phone: {
                required: helpers.withMessage('Please enter your phone', required),
                phone: helpers.withMessage('Value is not a valid phone', phone),
            },
            postcode: {
                required: helpers.withMessage('Please enter your postcode', required),
                postcode: helpers.withMessage('Value is not a valid postcode', postcode),
            },
            gdpr: {
                sameAs: helpers.withMessage('Please accept our T&C', sameAs(true)),
            },
        }, user);

        return {
            vehicle,
            user,
            v$,
            v2$,
        };
    },
    data() {
        return {
            flow: {
                images: [],
                loading: false,
                loadingModels: false,
                success: false,
                step: 1,
            },
            makes: [],
            models: [],
            version: 3,
            titleArticle: 'a',
            affil: 'regit',
            pageSource: '',
            vehicleType: '',
            finished: false,
            apiErrors: [] as String[],

            galleryInterval: null as any,
            galleryIndex: 0,
            galleryItemsTotal: 0,
        };
    },
    computed: {
        vehicleMakes(): DropdownOptions {
            return this.makes.map((item: any) => {
                return {value: '' + item.cman_code, text: item.cman_name};
            });
        },
        vehicleModels(): DropdownOptions {
            return this.models.map((item: any) => {
                return {value: '' + item.cran_code, text: item.cran_name};
            });
        },
        vehicleMakeName(): string {
            const makeName = this.makeName;
            if (makeName) {
                return makeName;
            }

            const option = this.vehicleMakes.find((item: DropdownOption) => item.value === this.vehicle.make);
            if (!option || option.value === '') {
                return '';
            }

            if (option.text.toLowerCase()[0] === 'a') {
                this.titleArticle = 'an';
            } else {
                this.titleArticle = 'a';
            }

            return option.text;
        },
        vehicleModelName(): string {
            const modelName = this.modelName;
            if (modelName) {
                return modelName;
            }

            if (this.flow.loadingModels) {
                return '';
            }

            const option = this.vehicleModels.find((item: DropdownOption) => item.value === this.vehicle.model);
            if (!option || option.value === '') {
                return '';
            }

            return option.text;
        },
    },
    methods: {
        checkVehicleType() {
            const url = window.location.href;
            if (url.includes('van')) {
                this.vehicleType = 'van';
            } else {
                this.vehicleType = 'car';
            }
        },
        getMakes() {
            if (this.vehicleType === 'van') {
                return axiosClient.get('/api/cap/van/makes?type=new').then((response) => {
                    this.makes = response.data;
                }, () => {
                    this.makes = [];
                });
            } else {
                return axiosClient.get('/api/cap/car/makes?type=new').then((response) => {
                    this.makes = response.data;
                }, () => {
                    this.makes = [];
                });
            }
        },
        getModels() {
            if (this.vehicle.make === '') {
                return;
            }

            this.flow.loadingModels = true;

            let getModels;

            if (this.vehicleType === 'van') {
                getModels = '/api/cap/van/makes/' + this.vehicle.make + '/models?type=new';
            } else {
                getModels = '/api/cap/car/makes/' + this.vehicle.make + '/models?type=new';
            }

            axiosClient.get(getModels).then((response) => {
                this.models = response.data;
                this.flow.loadingModels = false;
            }, () => {
                this.models = [];
                this.flow.loadingModels = false;
            });
        },
        getImages() {
            let getImagesUrl = '/api/imagin-studio/' + this.vehicleType + '/makes/';

            if (this.vehicle.make !== '' && this.vehicle.model !== '') {
                getImagesUrl += this.vehicle.make + '/models/' + this.vehicle.model + '/images';
            } else if (this.vehicle.make !== '') {
                getImagesUrl += this.vehicle.make + '/images';
            } else {
                return;
            }

            axiosClient.get(getImagesUrl).then((response) => {
                if (response.data.status === 'success') {
                    this.flow.images = response.data.images ?? [];
                    this.galleryItemsTotal = this.flow.images.length;
                    this.resetGallerySlider();
                }
            });
        },
        submitData() {
            const formData = {
                // step 1
                make: this.vehicleMakeName,
                model: this.vehicleModelName,
                intent: this.vehicle.intent,
                affil: this.affil,
                pageSource: this.pageSource,

                // step 2
                name: this.user.name,
                email: this.user.email,
                phone: this.user.phone,
                postcode: this.user.postcode,
                gdpr: this.user.gdpr ? 'accepted' : '',
            };

            axiosClient.post('/api/new-car-flow/submit', formData).then(() => {
                this.finished = true;
                this.flow.loading = true;
                this.flow.success = true;

                let label = 'Unknown';
                if (formData.intent === 'test-drive') {
                    label = 'Test Drive';
                } else if (formData.intent === 'research' || formData.intent === 'generic') {
                    label = 'Generic';
                } else if (formData.intent === 'quote') {
                    label = 'Quote';
                } else if (formData.intent === 'brochure') {
                    label = 'Brochure';
                } else if (formData.intent === 'callback') {
                    label = 'Callback';
                }
                window.sendAnalyticsEvent('Conversion Tracking', 'New Car Flow', label);

                window.onFacebookPixelLoaded(() => {
                    window.fbq('track', 'Lead', {
                        content_category: 'Test Drive',
                        content_name: 'Landing Page',
                    });
                });

                // Bing tracking, is passed only when bing loads
                window.uetq = window.uetq || [];
                window.uetq.push(
                    'event',
                    'Conversion_Tracking',
                    {
                        'event_category': 'New_Car_Flow',
                        'event_label': label.replace(' ', '_'),
                    },
                );
            }, (response) => {
                const apiErrors = response.response.data.errors || {};
                const newErrors = [] as String[];

                Object.keys(apiErrors).forEach((field) => {
                    const errors = apiErrors[field];
                    errors.forEach((error: string) => {
                        newErrors.push(error);
                    });
                });

                this.apiErrors = newErrors;
                this.flow.loading = false;
            });
        },
        async submit(scope: any) {
            let result = false;
            if (scope === 'step1') {
                result = await this.v$.$validate();
            } else if (scope === 'step2') {
                result = await this.v2$.$validate();
            }

            if (result && this.flow.step === 1) {
                window.sendAnalyticsEvent('Conversion Tracking', 'New Car Flow', 'Go to step 2');
                this.flow.step = 2;
                return;
            }

            if (result) {
                this.flow.loading = true;
                this.submitData();
            }
        },
        initValues() {
            if (this.intent) {
                this.vehicle.intent = this.intent;
            }
            if (this.make) {
                this.updateMake(this.make);
            }
            if (this.model) {
                this.updateModel(this.model);
            }
            if (this.step) {
                this.flow.step = parseInt(this.step);
            }

            // Convert this.defaultImages to this.flow.images iterating every element and extracting the image property.
            this.flow.images = this.defaultImages.map((item: any) => item.image);
        },
        nextImage() {
            if (this.galleryIndex + 1 > this.galleryItemsTotal - 1) {
                this.galleryIndex = 0;
            } else {
                this.galleryIndex = this.galleryIndex + 1;
            }
        },
        emptyMakeModel() {
            return (!this.vehicle.make.length) &&
                (!this.vehicle.model.length);
        },
        getRandomInt(min: number, max: number) {
            return Math.floor(Math.random() * (max - min + 1)) + min;
        },
        updateMake(make: string) {
            if (make) {
                this.vehicle.make = make;
                this.models = [];
                this.vehicle.model = '';
                this.getModels();
                this.getImages();
            }
        },
        updateModel(model: string) {
            if (model) {
                this.vehicle.model = model;
                this.getImages();
                this.refreshPageAdverts();
                this.sendToLytics();
            }
        },
        refreshPageAdverts() {
            if (this.vehicleMakeName || this.vehicleModelName) {
                adverts.retargetAdverts({
                    make: this.vehicleMakeName,
                    model: this.vehicleModelName,
                });
            }
        },
        sendToLytics() {
            if (targetingCookiesAllowed()) {
                window.onLyticsLoaded(() => {
                    window.jstag.send({
                        last_vrm: '',
                        last_make: this.vehicleMakeName,
                        last_model: this.vehicleModelName,
                        last_source: 'test-drive',
                    });
                });
            }
        },
        setPageSource() {
            const currentUrl = window.location.href;

            if (currentUrl.includes('van-test-drive')) {
                this.pageSource = 'van-test-drive';
            } else if (currentUrl.includes('new-car')) {
                this.pageSource = 'new-car';
            } else if (currentUrl.includes('test-drive')) {
                this.pageSource = 'test-drive';
            }
        },
        resetGallerySlider() {
            if (this.galleryInterval) {
                clearInterval(this.galleryInterval);
                this.galleryIndex = 0;
            }

            const totalImages = this.flow.images.length;

            this.changeGalleryImage(totalImages);

            if (totalImages > 1) {
                this.galleryInterval = setInterval(this.changeGalleryImage, 8000);
            }
        },
        changeGalleryImage(totalImages: number) {
            if (totalImages === 0) {
                return;
            }
            this.nextImage();
            const imageUrl = this.flow.images[this.galleryIndex];
            const gallery = document.querySelector('.hero-v2-new-car') as HTMLElement;
            gallery.style.setProperty('--background-opacity', '0');

            setTimeout(() => {
                gallery.style.setProperty('--background-image', `url(${imageUrl})`);
                gallery.style.setProperty('--background-opacity', '1');
            }, 850);
        },
    },
    async mounted() {
        this.checkVehicleType();
        await this.getMakes();
        this.initValues();
        this.setPageSource();

        this.galleryItemsTotal = this.defaultImages.length;
        this.resetGallerySlider();
    },
}, window.propsData);

connectVue(app);
configureGlobalComponents(app);
app.mount('#new-car-flow');
